import { CSSProperties } from 'react'
import { Dimensions, MultiPaneItem, MultiPanesConfig, MultiPaneStyles, Offsets } from './multiPaneLayout.interface'


const getPaneStyle = (paneId: number, index: number, parent: MultiPaneItem, parentDimensions: Dimensions, offsets: Offsets, parentOffsets: Offsets, dividerThickness: number, panesConfig: MultiPanesConfig, paneStyles: MultiPaneStyles = {}): MultiPaneStyles => {
    const paneItem = panesConfig[paneId]
    const { space, panes, horizontal } = paneItem
    const { panes: parentPanes, horizontal: parentHorizontal } = parent
    const style: CSSProperties = {
        width: parentDimensions.width,
        height: parentDimensions.height,
        left: offsets.left,
        top: offsets.top
    }
    if (parentPanes.length) {
        let dimensionAttribute: keyof Dimensions = 'width'
        let positionAttribute: keyof Offsets = 'left'
        if (!parentHorizontal) {
            dimensionAttribute = 'height'
            positionAttribute = 'top'
        }

        let paneDimension = ((parentDimensions[dimensionAttribute] as number) - (parentPanes.length - 1) * dividerThickness) * space / 100
        if (index == parentPanes.length - 1) {
            paneDimension = (parentDimensions[dimensionAttribute] as number) - ((offsets[positionAttribute] as number) - (parentOffsets[positionAttribute] as number)) - dividerThickness
        }

        style[dimensionAttribute] = paneDimension
        if (index !== 0) (style[positionAttribute] as number) += dividerThickness
    }
    let paneStyle: CSSProperties
    panes.forEach(({ id }, i) => {
        const pane = panesConfig[id]
        let dimensionAttribute: keyof Dimensions = 'width'
        let positionAttribute: keyof Offsets = 'left'
        if (!horizontal) {
            dimensionAttribute = 'height'
            positionAttribute = 'top'
        }
        let offsets: Offsets = { left: style.left, top: style.top }
        if (i > 0) {
            offsets = { left: paneStyle.left, top: paneStyle.top };
            (offsets[positionAttribute] as number) += (paneStyle[dimensionAttribute] as number)
        }
        paneStyle = getPaneStyle(pane.id, i, paneItem, {
            width: style.width,
            height: style.height
        }, offsets, { left: style.left, top: style.top }, dividerThickness, panesConfig, paneStyles)[pane.id]
    })
    paneStyles[paneItem.id] = style
    return paneStyles
}

export const getMultiPaneLayoutStyles = (paneId: number, width: number, height: number, panesConfig: MultiPanesConfig): Record<string, CSSProperties> => {
    const styles: Record<string, CSSProperties> = getPaneStyle(
        paneId,
        0,
        {
            id: 123,
            panes: [],
            horizontal: true,
            space: 100
        },
        { width, height },
        { left: 0, top: 0 },
        { left: 0, top: 0 },
        1,
        panesConfig
    )

    return styles
}

export const generatePaneLayoutsMap = (pane: MultiPaneItem, paneLayouts: Record<string, MultiPaneItem> = {}) => {
    paneLayouts[pane.id] = pane
    if (pane.panes.length) {
        pane.panes.forEach((v) => generatePaneLayoutsMap(v, paneLayouts))
    }
    return paneLayouts
}

export const countVisiblePanes = (pane?: MultiPaneItem | null): number => {
    if (!pane) return 0
    if (!pane.panes?.length) return 1
    return Object.values(generatePaneLayoutsMap(pane)).filter((p) => !p.panes?.length).length
}

export const updatePanesOnResize = (parentId: number, dividerIndex: number, deltaX: number, deltaY: number, panesConfig: MultiPanesConfig, previousStyles: MultiPaneStyles): MultiPanesConfig => {
    const pane = panesConfig[parentId]
    const newPanesConfig = {
        ...panesConfig
    }
    const { panes, horizontal } = pane
    const pane1 = panes[dividerIndex], pane2 = panes[dividerIndex + 1]
    const pane1Style = { ...previousStyles[pane1.id] }, pane2Style = { ...previousStyles[pane2.id] },
        parentStyle = previousStyles[parentId]

    if (horizontal) {
        const delta = deltaX > 0 ? Math.min((pane2Style.width as number), deltaX) : Math.max(deltaX, (-1) * (pane1Style.width as number))
        const newPane1Width = (pane1Style.width as number) + delta
        const newPane2Width = (pane1Style.width as number) - delta
        const parentWidth = parentStyle.width as number
        const pane1Percent = newPane1Width * 100 / parentWidth
        const pane2Percent = newPane2Width * 100 / parentWidth
        newPanesConfig[pane1.id] = {
            ...newPanesConfig[pane1.id],
            space: pane1Percent
        }
        newPanesConfig[pane2.id] = {
            ...newPanesConfig[pane2.id],
            space: pane2Percent
        }
    } else {
        const delta = deltaY > 0 ? Math.min((pane2Style.height as number), deltaY) : Math.max(deltaY, (-1) * (pane1Style.height as number))
        const newPane1Height = (pane1Style.height as number) + delta
        const newPane2Height = (pane1Style.height as number) - delta
        const parentHeight = parentStyle.height as number
        const pane1Percent = newPane1Height * 100 / parentHeight
        const pane2Percent = newPane2Height * 100 / parentHeight
        newPanesConfig[pane1.id] = {
            ...newPanesConfig[pane1.id],
            space: pane1Percent
        }
        newPanesConfig[pane2.id] = {
            ...newPanesConfig[pane2.id],
            space: pane2Percent
        }
    }
    return newPanesConfig
}

export const updatePanesToEqualSize = (parentId: number, panesConfig: MultiPanesConfig): MultiPanesConfig => {
    const parentPane = panesConfig[parentId]
    if (!parentPane || parentPane.panes.length <= 1) {
        return panesConfig
    }

    const newPanesConfig = { ...panesConfig }
    const equalSpace = 100 / parentPane.panes.length

    parentPane.panes.forEach(({ id }) => {
        newPanesConfig[id] = {
            ...newPanesConfig[id],
            space: equalSpace
        }
    })

    return newPanesConfig
}

export const updatePanesOnSplit = (paneId: number, numberOfDivisions: number, horizontal: boolean, panesConfig: MultiPanesConfig, parentPaneId?: number): MultiPanesConfig => {
    const newPanesConfig = {
        ...panesConfig
    }
    const pane = { ...newPanesConfig[paneId] }
    const parentPane = parentPaneId ? { ...newPanesConfig[parentPaneId] } : null
    const appendToParent = false && horizontal == parentPane?.horizontal
    const ids = Object.keys(newPanesConfig).map(v => parseInt(v))
    let lastId = Math.max(...ids)
    const newParentPaneId = ++lastId

    const newPanes: MultiPaneItem[] = []
    for (let c = 0; c < numberOfDivisions; c++) {
        const id = c === 0 ? paneId : ++lastId
        const newPane = {
            id: id,
            space: 100 / numberOfDivisions,
            panes: [],
            horizontal: true
        }
        newPanesConfig[id] = newPane
        newPanes.push(newPane)
    }

    if (appendToParent) {

    } else {
        pane.id = newParentPaneId
        pane.panes = newPanes
        pane.horizontal = horizontal
        if (parentPane) {
            parentPane.panes = parentPane.panes.map((v) => {
                return v.id == paneId ? { ...v, id: pane.id } : { ...v }
            })
            newPanesConfig[parentPane.id] = { ...parentPane }
        }
    }
    newPanesConfig[pane.id] = { ...pane }

    return newPanesConfig
}

export const updatePanesOnDelete = (deletedPaneId: number, parentPaneId: number, panesConfig: MultiPanesConfig): MultiPanesConfig => {
    const newPanesConfig = {
        ...panesConfig
    }
    const parentPane = { ...newPanesConfig[parentPaneId] }
    delete newPanesConfig[deletedPaneId]

    parentPane.panes = parentPane.panes.filter((v) => v.id !== deletedPaneId)

    if (parentPane.panes.length == 1) { // sibling present
        const remainingPane = { ...parentPane.panes[0] }
        const secondParent = findParentPane(parentPane.id, panesConfig)
        parentPane.id = remainingPane.id
        parentPane.panes = remainingPane.panes
        parentPane.horizontal = remainingPane.horizontal
        if (secondParent) {
            const secondParentId = secondParent.id
            newPanesConfig[secondParentId] = {
                ...secondParent,
                panes: secondParent.panes.map(v => {
                    return {
                        ...v,
                        id: v.id == parentPaneId ? parentPane.id : v.id
                    }
                })
            }
        }
        delete newPanesConfig[parentPaneId]
    }
    newPanesConfig[parentPane.id] = {
        ...parentPane
    }

    return newPanesConfig
    // const newStyleContext = getMultiPaneLayoutStyles(paneLayoutsRef.current[layout.id], container!.clientWidth, container!.clientHeight)
    // setStyleContext(newStyleContext)

}

export const findActivePaneOnDelete = (paneId: number, panesConfig: MultiPanesConfig): number => {
    let parent = findParentPane(paneId, panesConfig)
    while (true) {
        if (!parent) return 0
        if (parent.panes.length > 1) {
            const [nextPane] = parent.panes.filter(p => p.id !== paneId)
            if (nextPane.panes.length > 1) {
                return findFirstLeftNode(nextPane)?.id
            } else {
                return nextPane.id
            }
        } else {
            parent = findParentPane(parent.id, panesConfig)
        }
    }
}

export const findParentPane = (paneId: number, panesConfig: MultiPanesConfig) => {
    const panes = Object.values(panesConfig)
    const [parentPane] = panes.filter(v => v.panes.filter((p => p.id == paneId)).length !== 0)
    return parentPane
}

export const findFirstLeftNode = (rootNode: MultiPaneItem): MultiPaneItem => {
    let firstNode = rootNode
    while (true) {
        if (firstNode.panes.length == 0) break
        firstNode = firstNode.panes[0]
    }
    return firstNode
}

export const generateLayoutConfig = (panesConfig: MultiPanesConfig, root: MultiPaneItem): MultiPaneItem => {

    root.panes = root.panes.map(v => {
        const child = { ...panesConfig[v.id] }
        generateLayoutConfig(panesConfig, child)
        return child
    })

    return root

}


export const getDefaultPane = (): MultiPaneItem => {
    return {
        id: 1,
        horizontal: true,
        space: 100,
        panes: []
    }
}